import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { PlusIcon } from "vega-icons";
import * as Yup from "yup";

import { useGetCustomHeaders } from "@vega-api/custom/stores/useGetCustomHeaders";
import { useFeatureFlag } from "@vega-api/extended/auth/useFeatureFlag";
import { useGetFunds } from "@vega-api/generated/fund-management-v1/fund-management-v1";
import {
  getFundsV2,
  getGetFundsV2QueryKey,
} from "@vega-api/generated/fund-management-v2/fund-management-v2";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { type BaseMultiSelectOption } from "@vega-core/components/Form/Controls/Dropdown/ThemedMultiselectDropdown/ThemedMultiselectDropdown";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { FullScreenModal } from "@vega-core/components/Modals/FullScreenModal";
import { FullScreenModalHeader } from "@vega-core/components/Modals/FullScreenModalHeader";
import { Accordion } from "@vega-core/components/shadcn/accordion";
import { Key } from "@vega-core/constants/global";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { useDebounce } from "@vega-core/utils/useDebounce";

import { useCollaborators } from "../hooks/useCollaborators";

import { Collaborators } from "./Collaborators";

const FUNDS_PAGE_SIZE = 50;
const FUNDS_SEARCH_DEBOUNCE_MS = 300;

type Props = {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  rolesOptions: BaseMultiSelectOption[];
};

export type CollaboratorsState = {
  id: number;
  index: number;
  applyAllFund: boolean;
  selectedFunds: BaseMultiSelectOption[];
  selectedRole: BaseMultiSelectOption;
};

export const InviteCollaboratorsDialog = ({
  isOpen,
  rolesOptions,
  setOpen,
}: Props) => {
  const {
    collaboratorsAccordions,
    handleFundSelectionChange,
    handleApplyAllFundsToggle,
    handleRoleChange,
    deleteCollaboratorsAccordion,
    handleAddAccordion,
    isValid: isCollaboratorsConfigValid,
    handleInviteCollaborators,
    isPending,
    handleClose,
  } = useCollaborators(setOpen);

  const { featureFlag } = useFeatureFlag();
  const isV2FundsEnabled = featureFlag.fund_management_v2_endpoints;

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(
    searchQuery,
    FUNDS_SEARCH_DEBOUNCE_MS,
  );

  const { impersonatedUserId, limitedPartnerId } = useGetCustomHeaders();
  const { data: v1Funds = [] } = useGetFunds(
    { impersonatedUserId, limitedPartnerId },
    {
      query: {
        enabled: !isV2FundsEnabled,
        select: (response) =>
          response.data?.sort((a, b) =>
            (a.name.attribute || "").localeCompare(b.name.attribute || ""),
          ),
      },
    },
  );

  const {
    data: v2FundsData,
    fetchNextPage,
    hasNextPage: v2HasNextPage,
    isFetchingNextPage: v2IsFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: getGetFundsV2QueryKey({
      pageSize: FUNDS_PAGE_SIZE,
      searchQuery: debouncedSearchQuery || undefined,
    }),
    queryFn: ({ pageParam }) =>
      getFundsV2({
        page: pageParam,
        pageSize: FUNDS_PAGE_SIZE,
        searchQuery: debouncedSearchQuery || undefined,
      }),
    enabled: isV2FundsEnabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const meta = lastPage.meta;

      if (!meta?.hasNextPage) return undefined;

      return (meta.page ?? 1) + 1;
    },
  });

  const fundOptions: OptionType[] = isV2FundsEnabled
    ? (v2FundsData?.pages.flatMap((page) =>
        (page.data ?? [])
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          .filter((fund) => fund.name && fund.fundingStatus)
          .map((fund) => ({
            label: fund.name.attribute || "-",
            value: fund.id,
            assetClass: fund.attributes.assetClass,
          })),
      ) ?? [])
    : v1Funds.map((fund) => ({
        label: fund.name.attribute || "-",
        value: fund.id,
        assetClass: fund.attributes.assetClass,
      }));

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(m.settings_collaborators_validEmail())
      .email(m.settings_collaborators_validEmail()),
  });

  const initialValues = {
    email: "",
  };

  return (
    <FullScreenModal
      open={isOpen}
      setOpen={handleClose}
      skipCloseGuards
      variant="primary"
    >
      <div
        className="flex flex-col h-full w-full"
        data-testid="invite-collaborators-dialog"
      >
        <div className="flex items-center gap-6 h-20 border-b-1 border-border/primary w-full px-2 md:px-6">
          <FullScreenModalHeader
            setIsOpen={handleClose}
            title={m.settings_collaborators_inviteCollaborators()}
          />
        </div>
        <Formik
          initialValues={initialValues}
          onSubmit={handleInviteCollaborators}
          validationSchema={validationSchema}
        >
          {({ isValid: isFormValid, values, submitForm }) => (
            <Form
              className="flex flex-col flex-1"
              onKeyDown={(e) => {
                if (e.key === Key.Enter) {
                  e.preventDefault();
                }
              }}
            >
              <div className="flex-1 overflow-y-auto py-6 px-2 md:px-6">
                <div className="flex flex-col items-center">
                  <div className="w-full max-w-lg">
                    <InputField
                      label={m.settings_collaborators_inviteCollaboratorEmail()}
                      name="email"
                      placeholder={m.settings_collaborators_inviteCollaboratorEmailPlaceholder()}
                    />
                  </div>
                  <Accordion
                    className="flex flex-col gap-4 w-full max-w-lg mt-6 mb-4"
                    defaultValue={[collaboratorsAccordions[0]?.id.toString()]}
                    type="multiple"
                  >
                    {collaboratorsAccordions.length > 0 &&
                      collaboratorsAccordions.map((collaboratorsAccordion) => (
                        <Collaborators
                          collaboratorsAccordion={collaboratorsAccordion}
                          deleteCollaboratorsAccordion={
                            deleteCollaboratorsAccordion
                          }
                          fundOptions={fundOptions}
                          fundsHasNextPage={
                            isV2FundsEnabled ? v2HasNextPage : undefined
                          }
                          fundsIsLoadingMore={
                            isV2FundsEnabled ? v2IsFetchingNextPage : undefined
                          }
                          fundsSearchQuery={
                            isV2FundsEnabled ? searchQuery : undefined
                          }
                          handleApplyAllFundsToggle={handleApplyAllFundsToggle}
                          handleFundSelectionChange={handleFundSelectionChange}
                          handleRoleChange={handleRoleChange}
                          key={collaboratorsAccordion.id}
                          onFundsLoadMore={
                            isV2FundsEnabled
                              ? () => {
                                  void fetchNextPage();
                                }
                              : undefined
                          }
                          onFundsSearchChange={
                            isV2FundsEnabled ? setSearchQuery : undefined
                          }
                          rolesOptions={rolesOptions}
                        />
                      ))}
                  </Accordion>
                  <div className="flex w-full max-w-lg">
                    <ThemedButton
                      ariaLabel="Add Role"
                      className="self-start"
                      dataTestId="add-colloborator-role-button"
                      icon={<PlusIcon className="w-4 h-4" />}
                      onClick={handleAddAccordion}
                      type="button"
                      variant="secondary"
                    >
                      {m.settings_collaborators_inviteCollaboratorAddRole()}
                    </ThemedButton>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end h-24 border-t-1 border-border/primary w-full px-13 bg-background/primary">
                <ThemedButton
                  dataTestId="invite-collaborators-button"
                  disabled={
                    !isFormValid ||
                    !isCollaboratorsConfigValid() ||
                    !values.email
                  }
                  isLoading={isPending}
                  onClick={submitForm}
                  type="button"
                >
                  {m.settings_collaborators_inviteCollaborators()}
                </ThemedButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </FullScreenModal>
  );
};
